/**
 * Alpine component: dashboardHome
 *
 * Gestisce la home dashboard (mission control):
 * - Lazy-load conteggi quick actions (documenti, ordini, wallet)
 * - Tab switching griglia corsi (active/completed/all)
 */
export function registerDashboardHome(Alpine) {
    Alpine.data('dashboardHome', () => ({
        // Quick action counts (null = loading, number = loaded)
        docsCount: null,
        walletBalance: null,
        walletActive: null,

        // Course tabs
        activeTab: 'active',
        tabCourses: {},
        tabLoading: false,

        init() {
            this.loadQuickCounts();
            this.animateTracker();
        },

        /**
         * Anima il triennio tracker quando entra in viewport:
         * 1. Ring: stroke-dashoffset + count-up del valore
         * 2. Progress bars: una dopo l'altra con 300ms di delay
         */
        animateTracker() {
            const tracker = this.$root.querySelector('.ik-triennio-tracker');
            if (!tracker) return;

            const animate = () => {
                // 1. Ring animation
                const ring = tracker.querySelector('.ik-triennio-tracker__ring-fill[data-target-offset]');
                const valueEl = tracker.querySelector('.ik-triennio-tracker__ring-value[data-target]');

                if (ring && valueEl) {
                    const targetOffset = parseFloat(ring.dataset.targetOffset);
                    const targetValue = parseFloat(valueEl.dataset.target);
                    const duration = 1800;

                    ring.style.strokeDashoffset = targetOffset;

                    const start = performance.now();
                    const countUp = (now) => {
                        const progress = Math.min((now - start) / duration, 1);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        valueEl.textContent = Math.round(eased * targetValue);
                        if (progress < 1) requestAnimationFrame(countUp);
                    };
                    requestAnimationFrame(countUp);
                }

                // 2. Progress bars — in sequenza con delay
                const rows = tracker.querySelectorAll('.ik-triennio-tracker__year-row');
                rows.forEach((row, i) => {
                    setTimeout(() => {
                        const fill = row.querySelector('.ik-triennio-tracker__year-bar-fill[data-target-width]');
                        const val = row.querySelector('.ik-triennio-tracker__year-bar-value[data-target-left]');
                        if (fill) fill.style.width = fill.dataset.targetWidth + '%';
                        if (val) {
                            val.style.left = val.dataset.targetLeft + '%';
                            val.style.opacity = '1';
                        }
                    }, 1200 + (i * 400));
                });
            };

            const observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    animate();
                    observer.disconnect();
                }
            }, { threshold: 0.3 });
            observer.observe(tracker);
        },

        /**
         * Fetch parallelo per i conteggi quick actions.
         * Documenti: fetch tutti e conta solo type=certificate (certificati ECM).
         */
        async loadQuickCounts() {
            const [docs, wallet] = await Promise.allSettled([
                this._apiFetch('/v1/users/me/documents'),
                this._apiFetch('/v1/users/me/wallet'),
            ]);

            if (docs.status === 'fulfilled') {
                const items = docs.value?._embedded?.items ?? [];
                this.docsCount = items.filter(d => d.type === 'certificate').length;
            } else {
                this.docsCount = 0;
            }

            if (wallet.status === 'fulfilled') {
                this.walletBalance = wallet.value?.balance ?? 0;
                this.walletActive = wallet.value?.active ?? false;
            } else {
                this.walletBalance = 0;
                this.walletActive = false;
            }
        },

        /**
         * Cambia tab e fetch i dati se non già in cache.
         * Il tab "active" è SSR — nessun fetch necessario.
         *
         * "Completati" e "Tutti" condividono un solo fetch: completati = le
         * iscrizioni con certificato emesso (sub.completed, come la label
         * "Completato" della card). Il filtro ?status=completed dell'API
         * esclude i corsi con data di fine passata, che restano completati.
         */
        async switchTab(tab) {
            if (tab === this.activeTab) return;
            this.activeTab = tab;

            // Tab SSR: nessun fetch
            if (tab === 'active') return;

            // Già in cache
            if (this.tabCourses[tab]) return;

            this.tabLoading = true;
            try {
                if (!this.tabCourses['all']) {
                    const data = await this._apiFetch('/v1/users/me/subscriptions?page_size=-1');
                    this.tabCourses['all'] = data?._embedded?.items ?? [];
                }
                this.tabCourses['completed'] = this.tabCourses['all'].filter(sub => !!sub.completed);
            } catch (e) {
                this.tabCourses[tab] = [];
            } finally {
                this.tabLoading = false;
            }
        },

        /**
         * Formatta saldo wallet in formato italiano: "123,45 €"
         */
        formatBalance(value) {
            if (value == null) return '...';
            return parseFloat(value).toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }) + '\u00A0\u20AC';
        },

        /**
         * Fetch generico con Auth-Token header.
         */
        async _apiFetch(path) {
            const apiBase = window.IKOS?.apiBase || '';
            const headers = { 'Accept': 'application/json' };

            const authToken = window.IKOS?.authToken;
            if (authToken && authToken !== true) {
                headers['Auth-Token'] = authToken;
            }

            const guestId = window.IKOS?.guestId;
            if (guestId) {
                headers['X-Guest-Id'] = guestId;
            }

            const res = await fetch(apiBase + path, { headers });
            if (!res.ok) throw new Error('API ' + res.status);
            return res.json();
        },
    }));
}
